/**
 * Buffer of `spatial` frames and interpolation of the relative offsets (§3.5):
 * - render at (serverNow - interpDelayMs), linear interpolation between the two frames around it;
 * - extrapolation at most MAX_EXTRAPOLATION_MS past the last frame, then freeze;
 * - flag teleport (2): no interpolation towards that sample (step), and the mixer snaps;
 * - yaw interpolated along the shortest angular path.
 */
import { SPATIAL_FLAG_TELEPORT, type SpatialMessage } from '../api/protocol';
import { lerp, lerpAngle, type Vec3 } from './math';

export const MAX_EXTRAPOLATION_MS = 200;
/** Samples older than this (relative to the render time) are dropped. */
const KEEP_HISTORY_MS = 1000;
/** A peer absent from frames for this long is dropped from the buffer. */
const FORGET_AFTER_MS = 5000;
/** Extrapolate only if the last two samples are close enough in time. */
const MAX_EXTRAPOLATION_BASE_MS = 500;

interface PeerSample {
  t: number;
  x: number;
  y: number;
  z: number;
  flags: number;
}

interface SelfSample {
  t: number;
  yaw: number;
  pitch: number;
}

export interface PeerSampleResult extends Vec3 {
  /** Flags of the most recent sample at or before the render time (or the first one). */
  flags: number;
  /** Server time of the most recent teleport sample already reached, or null. */
  teleportAt: number | null;
  /** Server time of the newest sample known for this peer. */
  lastT: number;
}

export class SpatialBuffer {
  private readonly peers = new Map<string, PeerSample[]>();
  private self: SelfSample[] = [];
  private lastFrameT = -Infinity;

  /** Adds a frame. Frames with t <= the last one are ignored (out of order / duplicate). */
  push(frame: Pick<SpatialMessage, 't' | 'self' | 'p'>): boolean {
    if (!(frame.t > this.lastFrameT)) return false;
    this.lastFrameT = frame.t;
    this.self.push({ t: frame.t, yaw: frame.self.yaw, pitch: frame.self.pitch });
    for (const [id, dx, dy, dz, flags] of frame.p) {
      let list = this.peers.get(id);
      if (!list) {
        list = [];
        this.peers.set(id, list);
      }
      list.push({ t: frame.t, x: dx, y: dy, z: dz, flags });
    }
    return true;
  }

  clear(): void {
    this.peers.clear();
    this.self = [];
    this.lastFrameT = -Infinity;
  }

  removePeer(id: string): void {
    this.peers.delete(id);
  }

  hasPeer(id: string): boolean {
    return this.peers.has(id);
  }

  get newestT(): number {
    return this.lastFrameT;
  }

  /** Interpolated listener orientation at render time, or null without frames. */
  sampleSelf(renderT: number): { yaw: number; pitch: number } | null {
    const s = this.self;
    if (s.length === 0) return null;
    const [a, b] = bracket(s, renderT);
    if (!b) return { yaw: a.yaw, pitch: a.pitch };
    const f = (renderT - a.t) / (b.t - a.t);
    return { yaw: lerpAngle(a.yaw, b.yaw, f), pitch: lerp(a.pitch, b.pitch, f) };
  }

  /** Interpolated offset of one peer at render time, or null if never seen. */
  samplePeer(id: string, renderT: number): PeerSampleResult | null {
    const s = this.peers.get(id);
    if (!s || s.length === 0) return null;
    const teleportAt = lastTeleportAt(s, renderT);
    const last = s[s.length - 1]!;
    if (renderT >= last.t) {
      const prev = s.length >= 2 ? s[s.length - 2]! : null;
      const canExtrapolate =
        prev !== null &&
        (last.flags & SPATIAL_FLAG_TELEPORT) === 0 &&
        last.t - prev.t > 0 &&
        last.t - prev.t <= MAX_EXTRAPOLATION_BASE_MS;
      if (!canExtrapolate) return { x: last.x, y: last.y, z: last.z, flags: last.flags, teleportAt, lastT: last.t };
      const ahead = Math.min(renderT - last.t, MAX_EXTRAPOLATION_MS);
      const f = ahead / (last.t - prev!.t);
      return {
        x: last.x + (last.x - prev!.x) * f,
        y: last.y + (last.y - prev!.y) * f,
        z: last.z + (last.z - prev!.z) * f,
        flags: last.flags,
        teleportAt,
        lastT: last.t,
      };
    }
    const [a, b] = bracket(s, renderT);
    if (!b) return { x: a.x, y: a.y, z: a.z, flags: a.flags, teleportAt, lastT: last.t };
    if (b.flags & SPATIAL_FLAG_TELEPORT) {
      // Step: hold the previous value until the teleport sample is reached.
      return { x: a.x, y: a.y, z: a.z, flags: a.flags, teleportAt, lastT: last.t };
    }
    const f = (renderT - a.t) / (b.t - a.t);
    return {
      x: lerp(a.x, b.x, f),
      y: lerp(a.y, b.y, f),
      z: lerp(a.z, b.z, f),
      flags: a.flags,
      teleportAt,
      lastT: last.t,
    };
  }

  peerIds(): string[] {
    return [...this.peers.keys()];
  }

  /** Drops samples older than renderT - 1 s, keeping at least the last two per list. */
  prune(renderT: number): void {
    const limit = renderT - KEEP_HISTORY_MS;
    const trim = <T extends { t: number }>(list: T[]) => {
      let i = 0;
      while (i < list.length - 2 && list[i + 1]!.t <= limit) i++;
      if (i > 0) list.splice(0, i);
    };
    trim(this.self);
    for (const [id, list] of this.peers) {
      // Peers missing from frames for a long time (unsubscribed) are forgotten.
      if (list[list.length - 1]!.t < renderT - FORGET_AFTER_MS) this.peers.delete(id);
      else trim(list);
    }
  }
}

/**
 * Returns [a, b] with a.t <= t < b.t. If t is before the first sample returns [first, undefined];
 * if after the last returns [last, undefined].
 */
function bracket<T extends { t: number }>(s: T[], t: number): [T, T | undefined] {
  if (t <= s[0]!.t) return [s[0]!, undefined];
  for (let i = s.length - 1; i >= 0; i--) {
    const a = s[i]!;
    if (a.t <= t) return [a, s[i + 1]];
  }
  return [s[0]!, undefined];
}

function lastTeleportAt(s: PeerSample[], renderT: number): number | null {
  for (let i = s.length - 1; i >= 0; i--) {
    const x = s[i]!;
    if (x.t <= renderT && x.flags & SPATIAL_FLAG_TELEPORT) return x.t;
  }
  return null;
}

/**
 * Estimates the server clock from frame arrival times: offset = min(localRecv - t) over a window
 * (the fastest frame has the least network delay).
 */
export class ServerClock {
  private samples: number[] = [];
  constructor(private readonly window = 50) {}

  observe(serverT: number, localNow: number): void {
    this.samples.push(localNow - serverT);
    if (this.samples.length > this.window) this.samples.shift();
  }

  /** null until the first frame. */
  serverNow(localNow: number): number | null {
    if (this.samples.length === 0) return null;
    return localNow - Math.min(...this.samples);
  }

  reset(): void {
    this.samples = [];
  }
}
